import { useState, type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  LayoutDashboard,
  Menu,
  Moon,
  Settings,
  Sun,
  Tags,
  Users,
  UserRound,
  X,
  type LucideIcon
} from 'lucide-react'
import logo from '@/assets/TradeOne.png'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { themeToggled } from '@/features/ui/uiSlice'
import { selectRecentExceptionCount } from '@/features/exceptions/exceptionsSlice'
import type { LinkStatus } from '@/features/connection/connectionSlice'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/cn'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/dealers', label: 'Dealers', icon: Users },
  { to: '/plans', label: 'Plans', icon: Tags },
  { to: '/exceptions', label: 'Exceptions', icon: AlertTriangle },
  { to: '/settings', label: 'Settings', icon: Settings }
]

const LINK_META: Record<LinkStatus, { text: string; dot: string; pulse: boolean }> = {
  idle: { text: 'Idle', dot: 'bg-subtle', pulse: false },
  checking: { text: 'Linking', dot: 'bg-caution', pulse: true },
  online: { text: 'Live', dot: 'bg-positive', pulse: false },
  offline: { text: 'Offline', dot: 'bg-negative', pulse: true }
}

function Brand(): ReactNode {
  return (
    <div className="flex shrink-0 items-center gap-2.5">
      <img src={logo} alt="" aria-hidden className="h-8 w-auto" />
      <span className="hidden leading-none sm:block">
        <span className="block text-[15px] font-bold tracking-tight text-fg">Trade One</span>
        <span className="label-caps block pt-0.5 text-accent">Admin</span>
      </span>
    </div>
  )
}

/** Active item gets the orange outline the desktop app uses for its own tabs. */
function navClasses(isActive: boolean): string {
  return cn(
    'group flex items-center gap-2 rounded-[5px] border px-3 py-1.5 text-[13px] font-medium transition-colors',
    isActive
      ? 'border-accent bg-accent/10 text-accent'
      : 'border-transparent text-fg hover:bg-raised hover:text-fg'
  )
}

function ExceptionCount({ isActive }: { isActive: boolean }): ReactNode {
  const count = useAppSelector(selectRecentExceptionCount)
  if (count === 0) return null
  return (
    <span
      className={cn(
        'rounded px-1.5 text-[10px] font-bold leading-4',
        isActive ? 'bg-accent text-accent-fg' : 'bg-negative/15 text-negative'
      )}
    >
      {count}
    </span>
  )
}

function NavItems({ onNavigate }: { onNavigate?: () => void }): ReactNode {
  return (
    <>
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} className={({ isActive }) => navClasses(isActive)}>
          {({ isActive }) => (
            <>
              <Icon
                size={14}
                aria-hidden
                className={isActive ? 'text-accent' : 'text-muted group-hover:text-fg'}
              />
              {label}
              {label === 'Exceptions' && <ExceptionCount isActive={isActive} />}
            </>
          )}
        </NavLink>
      ))}
    </>
  )
}

function LinkIndicator(): ReactNode {
  const navigate = useNavigate()
  const { status, lastSyncedAt, lastError } = useAppSelector((s) => s.connection)
  const meta = LINK_META[status]

  return (
    <button
      onClick={() => navigate('/settings')}
      title={lastError ?? (lastSyncedAt ? 'Last synced ' + formatRelative(lastSyncedAt) : undefined)}
      className="flex h-9 items-center gap-2 rounded-md border border-line bg-raised px-2.5 transition-colors hover:border-line-strong"
    >
      <span className="relative flex h-1.5 w-1.5">
        {meta.pulse && (
          <span
            className={cn('absolute inline-flex h-full w-full animate-ping rounded-full', meta.dot)}
            aria-hidden
          />
        )}
        <span className={cn('relative inline-flex h-1.5 w-1.5 rounded-full', meta.dot)} aria-hidden />
      </span>
      <span className="label-caps hidden text-muted sm:inline">{meta.text}</span>
    </button>
  )
}

export function TopNav(): ReactNode {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const theme = useAppSelector((s) => s.ui.theme)
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <header className="sticky top-0 z-30 rounded-[8px] border border-line bg-surface">
      <div className="flex h-[50px] items-center gap-3 px-3 sm:px-6">
        <Brand />

        <nav className="ml-auto hidden items-center gap-1 lg:flex">
          <NavItems />
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-3">
          <LinkIndicator />

          <button
            onClick={() => dispatch(themeToggled())}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            className="grid h-8 w-8 place-items-center rounded-md text-accent transition-colors hover:bg-raised"
          >
            {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          <button
            onClick={() => navigate('/settings')}
            aria-label="Open settings"
            className="grid h-9 w-9 place-items-center rounded-full border border-accent/60 text-accent transition-colors hover:bg-accent/10"
          >
            <UserRound size={16} />
          </button>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={menuOpen}
            className="grid h-9 w-9 place-items-center rounded-md border border-line bg-raised text-fg transition-colors hover:border-line-strong lg:hidden"
          >
            {menuOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="flex flex-col gap-1 border-t border-line px-4 py-3 lg:hidden">
          <NavItems onNavigate={() => setMenuOpen(false)} />
        </nav>
      )}
    </header>
  )
}
